import React from 'react';
import { useTranslation } from 'react-i18next';
import FamilyMemoryCard from '@/components/memories/FamilyMemoryCard';
import type { StoryType } from '@/stores/models/Story';
import { canEditOrDeleteStory } from '@/types/story';

import './memories-feed.css';

interface MemoriesFeedProps {
  stories: StoryType[];
  familyId: string;
  isLoading?: boolean;
  /** Fallback when the API did not resolve access for a memory; per-memory access wins. */
  canEdit?: boolean;
  /** Fallback when the API did not resolve access for a memory; per-memory access wins. */
  canDelete?: boolean;
  onEdit?: (storyId: string) => void;
  onDelete?: (storyId: string) => void;
  emptyTitle?: string;
  emptyBody?: string;
}

const MemoriesFeed: React.FC<MemoriesFeedProps> = ({
  stories,
  familyId,
  isLoading = false,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
  emptyTitle,
  emptyBody
}) => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const resolvedEmptyTitle = emptyTitle ?? t('memories.emptyTitle');
  const resolvedEmptyBody = emptyBody ?? t('memories.emptyBody');

  if (isLoading) {
    return <div className="memories-feed__loading">{tForms('story.loadingMemories')}</div>;
  }

  if (stories.length === 0) {
    return (
      <div className="memories-feed__empty">
        <h3 className="memories-feed__empty-title">{resolvedEmptyTitle}</h3>
        <p className="memories-feed__empty-body">{resolvedEmptyBody}</p>
      </div>
    );
  }

  return (
    <div className="memories-feed">
      {stories.map((story) => (
        <FamilyMemoryCard
          key={story.id}
          story={story}
          familyId={familyId}
          canEdit={canEditOrDeleteStory(story, canEdit)}
          canDelete={canEditOrDeleteStory(story, canDelete)}
          onEdit={onEdit ? () => onEdit(story.id) : undefined}
          onDelete={onDelete ? () => onDelete(story.id) : undefined}
        />
      ))}
    </div>
  );
};

export default MemoriesFeed;
